/**
 * Taxonomy picker: discipline + grade (owned by the header) → module → topic → lesson. The lesson
 * rides on the topic — changing the topic drops it. A curriculum re-import never remaps rows, and
 * the catalogue is the latest version only, so a saved topic may be absent from it: that saved
 * tagging is rendered from the row's denormalized labels as a "re-tag" state, never as a blank select.
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { CurriculumModuleOption, CurriculumTopicOption } from './interfaces'

import { useAssignmentEditorStore } from './store'
import { useCurriculumModulesApi } from '../../api/curriculum/modules.hook'
import { getModuleOptions, getTaxonomyLabel } from './assignment-template-options'

interface UseAssignmentTaxonomyPickerReturn {
  refIsLoading: Ref<boolean>
  refCanPick: ComputedRef<boolean>
  refModuleOptions: ComputedRef<AppSelectOption<number>[]>
  refTopicOptions: ComputedRef<AppSelectOption<number>[]>
  refLessonOptions: ComputedRef<AppSelectOption<number>[]>
  refModuleId: WritableComputedRef<number | undefined>
  refTopicId: WritableComputedRef<number | undefined>
  refLessonId: WritableComputedRef<number | undefined>
  refIsCatalogueEmpty: ComputedRef<boolean>
  /** The saved topic cannot be represented by the picker. */
  refNeedsRetagging: ComputedRef<boolean>
  refSavedLabel: ComputedRef<string | null>
  clearTopic: () => void
  reload: () => Promise<void>
}

export const useAssignmentTaxonomyPicker = (): UseAssignmentTaxonomyPickerReturn => {
  const store = useAssignmentEditorStore()

  const refModules = ref<CurriculumModuleOption[]>([])
  const refIsLoading = ref<boolean>(false)
  /** Keeps the stale banner from flashing before the catalogue arrives. */
  const refHasLoaded = ref<boolean>(false)
  /** `null` means "follow the saved topic". */
  const refBrowsingModuleId = ref<number | null>(null)

  const refCanPick = computed<boolean>(() =>
    store.getDisciplineCode !== null && store.getGrade !== null)

  /** Guards against out-of-order responses: only the latest pair's catalogue lands. */
  let requestSeq = 0

  const load = async (): Promise<void> => {
    const disciplineCode = store.getDisciplineCode
    const grade = store.getGrade
    const token = ++requestSeq

    if (!disciplineCode || !grade) {
      refModules.value = []
      refHasLoaded.value = false
      return
    }

    const modules = await useCurriculumModulesApi({
      disciplineCode,
      grade,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (token !== requestSeq) {
      return
    }

    // A failed request is not an empty curriculum: leave `refHasLoaded` false.
    if (!modules) {
      return
    }

    refModules.value = modules
    refHasLoaded.value = true
  }

  const refModuleOfSavedTopic = computed<number | null>(() => {
    const topicId = store.getCurriculumTopicId

    if (topicId === null) {
      return null
    }

    return refModules.value
      .find(module => module.topics.some(topic => topic.id === topicId))?.id ?? null
  })

  const refModuleOptions = computed<AppSelectOption<number>[]>(() => getModuleOptions(refModules.value))

  const refActiveModuleId = computed<number | null>(() =>
    refBrowsingModuleId.value ?? refModuleOfSavedTopic.value)

  const refTopicOptions = computed<AppSelectOption<number>[]>(() => {
    const moduleId = refActiveModuleId.value

    if (moduleId === null) {
      return []
    }

    return refModules.value
      .find(module => module.id === moduleId)?.topics
      .map(topic => ({ value: topic.id, text: topic.topic_name })) ?? []
  })

  const refModuleId = computed<number | undefined>({
    get: () => refActiveModuleId.value ?? undefined,
    set: (value) => {
      refBrowsingModuleId.value = value ?? null

      // Browsing to another module must not leave a topic selected that it does not contain.
      const topicId = store.getCurriculumTopicId

      if (topicId !== null && value !== undefined && value !== refModuleOfSavedTopic.value) {
        store.setCurriculumTopicId(null)
      }
    },
  })

  const refTopicId = computed<number | undefined>({
    // Only reported when the catalogue offers it; a stale id is shown by the banner instead.
    get: () => {
      const topicId = store.getCurriculumTopicId

      return topicId !== null && refTopicOptions.value.some(option => option.value === topicId)
        ? topicId
        : undefined
    },
    set: (value) => {
      store.setCurriculumTopicId(value ?? null)
    },
  })

  const refSelectedTopic = computed<CurriculumTopicOption | null>(() => {
    const topicId = store.getCurriculumTopicId

    if (topicId === null) {
      return null
    }

    return refModules.value
      .flatMap(module => module.topics)
      .find(topic => topic.id === topicId) ?? null
  })

  const refLessonOptions = computed<AppSelectOption<number>[]>(() =>
    // A topic routinely holds several lessons with the same name; the number separates them.
    (refSelectedTopic.value?.lessons ?? []).map(lesson => ({
      value: lesson.id,
      text: `${lesson.lesson_number}. ${lesson.name}`,
    })),
  )

  const refLessonId = computed<number | undefined>({
    // Same rule as the topic.
    get: () => {
      const lessonId = store.getCurriculumLessonId

      return lessonId !== null && refLessonOptions.value.some(option => option.value === lessonId)
        ? lessonId
        : undefined
    },
    set: (value) => {
      store.setCurriculumLessonId(value ?? null)
    },
  })

  const refIsCatalogueEmpty = computed<boolean>(() =>
    refHasLoaded.value && !refIsLoading.value && refModules.value.length === 0)

  const refNeedsRetagging = computed<boolean>(() => {
    const topicId = store.getCurriculumTopicId

    if (topicId === null) {
      return false
    }

    if (store.isStale) {
      return true
    }

    return refHasLoaded.value && refModuleOfSavedTopic.value === null
  })

  const refSavedLabel = computed<string | null>(() => {
    const labels = store.getTaxonomyLabels

    return labels ? getTaxonomyLabel(labels) : null
  })

  const clearTopic = (): void => {
    store.setCurriculumTopicId(null)
  }

  // Module ids do not survive a discipline/grade change; drop the browsing id with the catalogue.
  watch(
    [() => store.getDisciplineCode, () => store.getGrade],
    () => {
      refBrowsingModuleId.value = null
      refModules.value = []
      refHasLoaded.value = false
      void load()
    },
    { immediate: true },
  )

  return {
    refIsLoading,
    refCanPick,
    refModuleOptions,
    refTopicOptions,
    refLessonOptions,
    refModuleId,
    refTopicId,
    refLessonId,
    refIsCatalogueEmpty,
    refNeedsRetagging,
    refSavedLabel,
    clearTopic,
    reload: load,
  }
}
